import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@monksflow/monks-ui';
import { Link } from 'react-router';
import {
  InfoIcon,
  ListIcon,
  ListTreeIcon,
  PlugZapIcon,
  PlusIcon,
  TriangleAlertIcon,
} from 'lucide-react';
import { ROUTES } from '@/shared/config/routes';
import { useDebounce } from '@/shared/hooks';
import { FilterChipPopover } from '@/shared/ui';
import { CONNECTOR_PLATFORM_LABELS } from '@/entities/media-connector';
import { BLOCKING_CONNECTOR_HEALTH, CONNECTOR_HEALTH_ADVICE } from '@/entities/media-campaign';
import {
  advertiserLabel,
  connectorAccountsQueryOptions,
  connectorAdvertisersQueryOptions,
  connectorPagesQueryOptions,
  countGroup,
  flattenNodes,
  levelPluralLabel,
  nodeName,
  platformStructure,
  useAddJobRow,
  useLinkNodeToExisting,
  type JobPlatformGroup,
} from '@/entities/media-campaign';
import { notificationService } from '@/lib/services/notification.service';
import type { JobNode } from '@/entities/media-campaign';
import { allIssues, ancestorIdsOf, countAtLevel, countSeverity, leafLevelOf } from '../lib/flatten';
import { type PreflightStatus } from '../lib/preflight';

import { CreativePreviewDialog } from './CreativePreviewDialog';
import { JobIssuesPanel } from './JobIssuesPanel';
import { JobNodeDrawer } from './JobNodeDrawer';
import { JobPlatformTable } from './JobPlatformTable';
import { useTranslation } from 'react-i18next';

/** How long the outlined cell stays outlined after the drawer sends you there. */
const FOCUS_HOLD_MS = 2600;

/** The live node behind an id, wherever it sits in the plan tree. */
function findNode(group: JobPlatformGroup, nodeId: string): JobNode | null {
  const walk = (nodes: JobNode[]): JobNode | null => {
    for (const node of nodes) {
      if (node.id === nodeId) return node;
      const hit = walk(node.children);
      if (hit) return hit;
    }
    return null;
  };
  return walk(group.nodes);
}

export interface JobPlatformPanelProps {
  /** Needed to fetch the creative images the plan was uploaded with. */
  jobId: string;
  group: JobPlatformGroup;
  onEditField: (nodeId: string, key: string, value: string | null) => void;
  onBindAccount: (connectorId: string, accountName: string) => void;
  onBindPage: (pageId: string, pageName: string) => void;
  onBindAdvertiser: (advertiserId: string) => void;
  /** False until pre-flight has run for this Job. */
  showIssues: boolean;
  /** Fields edited since the last run get their findings marked out of date. */
  preflightStatus: PreflightStatus;
}

/**
 * Everything one platform gets from this Job: its account binding, its counts,
 * and its trafficking sheet.
 *
 * One platform at a time is the whole shape of the screen — the hierarchy, the
 * field names and the rules all change at this boundary — and the account
 * binding sits here, where its consequences are visible.
 */
export function JobPlatformPanel({
  jobId,
  group,
  onEditField,
  onBindAccount,
  onBindPage,
  onBindAdvertiser,
  showIssues,
  preflightStatus,
}: JobPlatformPanelProps) {
  const { t } = useTranslation('projects');
  const { data: accounts } = useQuery(connectorAccountsQueryOptions());
  // Empty for a platform that publishes as the account rather than a page, so the
  // chooser below appears only where there is actually a choice to make.
  const { data: pageList } = useQuery(connectorPagesQueryOptions(group.connectorId));
  const pages = pageList?.items;
  // The clients under a manager account (a Google Ads MCC holds 96 of them), searched on the
  // platform rather than here. `supported` is false wherever the account is itself the
  // advertiser, so the chooser appears only where there is a client to choose.
  const [clientSearch, setClientSearch] = useState('');
  const {
    data: clientList,
    isFetching: isFetchingClients,
    isError: clientsFailed,
    refetch: refetchClients,
  } = useQuery(connectorAdvertisersQueryOptions(group.connectorId, useDebounce(clientSearch, 300)));
  const linkNode = useLinkNodeToExisting(jobId);
  const addRow = useAddJobRow(jobId);
  /**
   * A row hangs off an ad set, and a plan usually has several — so the button asks
   * which rather than guessing, and stays out of the way where there is none to hang
   * it on.
   */
  const adSets = useMemo(
    () => flattenNodes(group.nodes).filter((node) => node.level === 'ad_group'),
    [group.nodes],
  );

  const [grouped, setGrouped] = useState(false);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set());
  /**
   * The drawer holds a node ID, not the node.
   *
   * Holding the object froze the drawer on the values it opened with: an edit
   * refetches the plan and every node is replaced, so the copy in state could
   * never reflect what was just saved. Resolving by id each render means the
   * drawer reads from the same plan the sheet does.
   */
  const [details, setDetails] = useState<{ nodeId: string; focusField: string | null } | null>(
    null,
  );
  const detailsNode = details === null ? null : findNode(group, details.nodeId);
  const [issuesNodeId, setIssuesNodeId] = useState<string | null>(null);
  const [isIssuesOpen, setIsIssuesOpen] = useState(false);
  /**
   * The preview holds a node ID too, and for the same reason the drawer does.
   *
   * It did not, and attaching an image from inside it appeared to do nothing: the
   * upload refetched the plan, every node was replaced, and the copy this held was
   * still the one captured when the dialog opened — so it kept reporting the creative
   * as having no image it had just been given.
   */
  const [previewNodeId, setPreviewNodeId] = useState<string | null>(null);
  const previewNode = previewNodeId === null ? null : findNode(group, previewNodeId);
  const [focusedCell, setFocusedCell] = useState<string | null>(null);
  /** Whether the account's state actually stops work, as opposed to merely reporting one. */
  const accountBlocks =
    group.connectorStatus !== null && BLOCKING_CONNECTOR_HEALTH.has(group.connectorStatus);

  const issues = useMemo(() => allIssues(group.platform, group), [group]);

  // The outline is a pointer, not a state — drop it once it has done its job so
  // a stale ring is not still sitting on a cell the reviewer already fixed.
  useEffect(() => {
    if (focusedCell === null) return;
    const timer = setTimeout(() => setFocusedCell(null), FOCUS_HOLD_MS);
    return () => clearTimeout(timer);
  }, [focusedCell]);

  const counts = countGroup(group);
  const severity = countSeverity(issues);
  const structure = platformStructure(group.platform);
  const options = (accounts ?? []).filter((item) => item.platform === group.platform);

  const shape = structure.order
    .map(
      (level) =>
        `${countAtLevel(group, level)} ${levelPluralLabel(group.platform, level).toLowerCase()}`,
    )
    .join(' · ');

  return (
    <section
      className="flex min-h-0 flex-1 flex-col gap-3 rounded-xl border border-border-light bg-card p-4"
      data-testid={`job-platform-${group.platform}`}
    >
      {/* No platform name or logo here — the tab above already carries both. */}
      <header className="flex shrink-0 flex-wrap items-center justify-between gap-3">
        <span className="text-xs text-muted-foreground">{shape}</span>

        <div className="flex flex-wrap items-center gap-2">
          {counts.create > 0 && (
            <Badge variant="positive" roundness="full" className="font-normal">
              {t('deliveryJobReview.jobPlatformPanel.count.new', { count: counts.create })}
            </Badge>
          )}
          {counts.update > 0 && (
            <Badge variant="secondary" roundness="full" className="font-normal">
              {t('deliveryJobReview.jobPlatformPanel.count.updated', { count: counts.update })}
            </Badge>
          )}
          {counts.unchanged > 0 && (
            <Badge variant="ghost" roundness="full" className="border-border-light font-normal">
              {t('deliveryJobReview.jobPlatformPanel.count.unchanged', {
                count: counts.unchanged,
              })}
            </Badge>
          )}
          {/* The only level-independent way into the drawer: a row badge can
              only be clicked on the level currently on screen, and the issues
              it points at are often on another one. Advice counts too, since on
              an otherwise clean plan this is the only way to reach it. */}
          {showIssues && severity.errors + severity.warnings + severity.advice > 0 && (
            <button
              type="button"
              onClick={() => {
                setIssuesNodeId(null);
                setIsIssuesOpen(true);
              }}
              data-testid="job-open-issues"
              className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-full"
            >
              {severity.errors + severity.warnings > 0 ? (
                <Badge
                  variant={severity.errors > 0 ? 'destructive' : 'warning'}
                  roundness="full"
                  className="cursor-pointer font-normal"
                >
                  <TriangleAlertIcon className="size-3" />
                  {severity.errors > 0
                    ? `${severity.errors} to fix`
                    : `${severity.warnings} to check`}
                </Badge>
              ) : (
                <Badge variant="secondary" roundness="full" className="cursor-pointer font-normal">
                  <InfoIcon className="size-3" />
                  {t('deliveryJobReview.jobPlatformPanel.count.forInformation', {
                    count: severity.advice,
                  })}
                </Badge>
              )}
            </button>
          )}
        </div>
      </header>

      {/* Before anything else on the panel, and before anything is run. A lapsed token
          used to be discoverable only by starting a check that then died on the worker,
          so the reviewer watched a spinner time out and was told nothing about why. */}
      {group.connectorStatus !== null && group.connectorStatus !== 'connected' && (
        <div
          role="alert"
          className={`flex shrink-0 flex-wrap items-center gap-2 rounded-lg border px-3 py-2 ${
            accountBlocks
              ? 'border-destructive/30 bg-destructive/5'
              : 'border-warning/30 bg-warning/5'
          }`}
          data-testid={`job-account-unusable-${group.platform}`}
        >
          <PlugZapIcon
            className={`size-4 shrink-0 ${accountBlocks ? 'text-destructive' : 'text-warning-foreground'}`}
            aria-hidden
          />
          <span
            className={`text-sm ${accountBlocks ? 'text-destructive' : 'text-warning-foreground'}`}
          >
            {CONNECTOR_HEALTH_ADVICE[group.connectorStatus]}
          </span>
          {/* Only where it is true. Saying it of an account that publishes fine is how a
              working DV360 connection read as broken for hours. */}
          {accountBlocks && (
            <span className="text-xs text-muted-foreground">
              {t('deliveryJobReview.jobPlatformPanel.nothingCanBeChecked', {
                platform: CONNECTOR_PLATFORM_LABELS[group.platform],
              })}
            </span>
          )}
          <Button variant="outline" size="sm" asChild className="ml-auto">
            <Link to={ROUTES.KNOWLEDGE_HUB_MEDIA_DETAIL(group.platform)}>
              {t('deliveryJobReview.jobPlatformPanel.reconnect')}
            </Link>
          </Button>
        </div>
      )}

      {/*
        A capability that is failing, in the platform's own words.

        Separate from the banner above because it is a different claim: the connection is
        fine and one thing it does is not. Reporting failing does not stop a publish at
        all — saying so is the entire reason this screen once told someone to reconnect a
        DV360 account that was publishing perfectly.
      */}
      {/* eslint-disable-next-line i18next/no-literal-string -- connector capability keys, not copy */}
      {(['publishing', 'reporting'] as const)
        .filter((capability) => group.connectorCapabilities?.[capability].state === 'failing')
        .map((capability) => (
          <div
            key={capability}
            role="status"
            className="flex shrink-0 flex-wrap items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2"
            data-testid={`job-capability-failing-${capability}-${group.platform}`}
          >
            <TriangleAlertIcon
              className="mt-0.5 size-4 shrink-0 text-warning-foreground"
              aria-hidden
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm text-warning-foreground">
                {capability === 'publishing'
                  ? t('deliveryJobReview.jobPlatformPanel.publishingFailing', {
                      platform: CONNECTOR_PLATFORM_LABELS[group.platform],
                    })
                  : t('deliveryJobReview.jobPlatformPanel.reportingFailing', {
                      platform: CONNECTOR_PLATFORM_LABELS[group.platform],
                    })}
              </p>
              {/* Selectable, and any URL in it clickable: Google's message carries the
                  console link that fixes the problem, and paraphrasing loses it. */}
              <p className="mt-0.5 text-xs break-words text-muted-foreground select-text">
                {group.connectorCapabilities?.[capability].error}
              </p>
            </div>
          </div>
        ))}

      {/*
        A platform that cannot be asked at all, which is a standing fact about the platform
        rather than anything wrong with this account or this plan.

        It sits here, before the sheet, because it changes how everything below should be
        read: on DV360 a clean pre-flight is our own rules agreeing with themselves, and the
        first opinion the platform ever gives is the publish creating live objects.
      */}
      {group.dryRunSupported === false && (
        <div
          role="status"
          className="flex shrink-0 flex-wrap items-start gap-2 rounded-lg border border-warning/30 bg-warning/5 px-3 py-2"
          data-testid={`job-no-dry-run-${group.platform}`}
        >
          <TriangleAlertIcon
            className="mt-0.5 size-4 shrink-0 text-warning-foreground"
            aria-hidden
          />
          <p className="min-w-0 flex-1 text-sm text-warning-foreground">
            {t('deliveryJobReview.jobPlatformPanel.noDryRun', {
              platform: CONNECTOR_PLATFORM_LABELS[group.platform],
            })}
          </p>
        </div>
      )}

      <div className="flex shrink-0 flex-wrap items-center gap-2 rounded-lg bg-surface px-3 py-2 dark:bg-surface-raised">
        {/* Flat is the default because the source file is flat; grouping is for
            plans with enough campaigns that folding beats scrolling. */}
        <div className="ml-auto order-last flex items-center gap-0.5 rounded-md bg-card p-0.5">
          {(
            [
              { value: false, label: t('deliveryJobReview.jobPlatformPanel.flat'), icon: ListIcon },
              {
                value: true,
                label: t('deliveryJobReview.jobPlatformPanel.hierarchical'),
                icon: ListTreeIcon,
              },
            ] as const
          ).map(({ value, label, icon: Icon }) => (
            <button
              key={label}
              type="button"
              aria-pressed={grouped === value}
              title={label}
              onClick={() => setGrouped(value)}
              data-testid={`job-view-${value ? 'grouped' : 'flat'}`}
              className={`flex items-center gap-1.5 rounded px-2 py-1 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                grouped === value
                  ? 'bg-surface text-foreground dark:bg-surface-raised'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="size-3.5" aria-hidden />
              {label}
            </button>
          ))}
        </div>

        <span className="text-xs text-muted-foreground">
          {t('deliveryJobReview.jobPlatformPanel.publishingInto')}
        </span>
        {options.length === 0 ? (
          <span className="text-xs text-destructive">
            {t('deliveryJobReview.jobPlatformPanel.noAccountConnected', {
              platform: CONNECTOR_PLATFORM_LABELS[group.platform],
            })}
          </span>
        ) : (
          <Select
            value={group.connectorId ?? ''}
            onValueChange={(next) => {
              const account = options.find((item) => item.id === next);
              if (account) onBindAccount(account.id, account.externalAccountName);
            }}
          >
            <SelectTrigger
              size="sm"
              error={group.connectorId === null}
              className="w-[min(100%,24rem)]"
              data-testid={`job-account-${group.platform}`}
            >
              <SelectValue
                placeholder={t('deliveryJobReview.jobPlatformPanel.selectAnAdAccount')}
              />
            </SelectTrigger>
            <SelectContent>
              {options.map((item) => (
                <SelectItem key={item.id} value={item.id} disabled={item.status === 'expired'}>
                  <span className="flex items-center gap-2">
                    {item.title}
                    {/* Connections can share one ad account; the title tells them apart. */}
                    {item.externalAccountName !== item.title && (
                      <span className="text-muted-foreground">{item.externalAccountName}</span>
                    )}
                    {item.status === 'expired' && (
                      <Badge variant="warning" roundness="full" className="font-normal">
                        {t('deliveryJobReview.jobPlatformPanel.reconnect')}
                      </Badge>
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {pageList?.supported && (
          <>
            <span className="text-xs text-muted-foreground">
              {t('deliveryJobReview.jobPlatformPanel.publishingAs')}
            </span>
            {pages && pages.length === 0 ? (
              // Said in the row rather than behind a tooltip: it is the reason nothing will
              // publish, and a reason worth hovering for is a reason nobody reads.
              <span
                className="flex flex-wrap items-center gap-1.5"
                data-testid={`job-page-none-${group.platform}`}
              >
                <Badge variant="destructive" roundness="full" className="font-normal">
                  <TriangleAlertIcon className="size-3" aria-hidden />
                  {t('deliveryJobReview.jobPlatformPanel.noPage')}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {t('deliveryJobReview.jobPlatformPanel.noPageDetail')}
                </span>
              </span>
            ) : (
              <Select
                value={group.pageId ?? (pages?.length === 1 ? pages[0].id : '')}
                disabled={!pages || pages.length <= 1}
                onValueChange={(next) => {
                  const page = pages?.find((item) => item.id === next);
                  if (page) onBindPage(page.id, page.name);
                }}
              >
                <SelectTrigger
                  size="sm"
                  className="w-[min(100%,16rem)]"
                  data-testid={`job-page-${group.platform}`}
                >
                  <SelectValue
                    placeholder={
                      pages
                        ? t('deliveryJobReview.jobPlatformPanel.selectAPage')
                        : t('deliveryJobReview.jobPlatformPanel.loading')
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  {pages?.map((item) => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </>
        )}

        {group.connectorId && (clientsFailed || clientList?.supported) && (
          <>
            <span className="text-xs text-muted-foreground">
              {t('deliveryJobReview.jobPlatformPanel.forClient')}
            </span>
            {/* A failed list is shown with a way back, never hidden: a hidden chooser is what
                left a manager account with no way to publish. */}
            {clientsFailed ? (
              <span role="alert" className="flex items-center gap-1.5">
                <span className="text-xs text-destructive">
                  {t('deliveryJobReview.jobPlatformPanel.clientsCouldNotLoad')}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={isFetchingClients}
                  onClick={() => void refetchClients()}
                >
                  {t('deliveryJobReview.jobPlatformPanel.retryClients')}
                </Button>
              </span>
            ) : (
              <span data-testid={`job-client-${group.platform}`}>
                <FilterChipPopover<string>
                  mode="single"
                  label={t('deliveryJobReview.jobPlatformPanel.forClient')}
                  triggerLabelStyle="value"
                  triggerAriaLabel={t('deliveryJobReview.jobPlatformPanel.selectAClient')}
                  searchPlaceholder={t('deliveryJobReview.jobPlatformPanel.searchClients')}
                  hideReset
                  // Also what the trigger shows while the chosen client is outside the
                  // current page of results, so a search never blanks the choice.
                  resetOptionLabel={
                    group.advertiserId
                      ? advertiserLabel({
                          id: group.advertiserId,
                          name: group.advertiserName ?? group.advertiserId,
                        })
                      : t('deliveryJobReview.jobPlatformPanel.selectAClient')
                  }
                  options={clientList.items.map((item) => ({
                    value: item.id,
                    label: advertiserLabel(item),
                  }))}
                  value={group.advertiserId ?? null}
                  onChange={(next) => {
                    if (next) onBindAdvertiser(next);
                  }}
                  asyncOptions={{
                    search: clientSearch,
                    onSearchChange: setClientSearch,
                    isLoading: isFetchingClients,
                  }}
                  frameClassName={group.needsAdvertiser ? 'border-destructive' : undefined}
                />
              </span>
            )}
          </>
        )}
      </div>

      <JobPlatformTable
        jobId={jobId}
        group={group}
        grouped={grouped}
        collapsed={collapsed}
        onToggleCollapsed={(nodeId) =>
          setCollapsed((prev) => {
            const next = new Set(prev);
            if (!next.delete(nodeId)) next.add(nodeId);
            return next;
          })
        }
        onEditField={onEditField}
        onOpenDetails={(node) => {
          setIsIssuesOpen(false);
          setDetails({ nodeId: node.id, focusField: null });
        }}
        onOpenIssues={(nodeId) => {
          setDetails(null);
          setIssuesNodeId(nodeId);
          setIsIssuesOpen(true);
        }}
        onPreviewCreative={(node) => setPreviewNodeId(node.id)}
        focusedCell={focusedCell}
        showIssues={showIssues}
        preflightStatus={preflightStatus}
      />

      {/*
        Under the table, where a new line goes. Blank on arrival and failing pre-flight
        until it is filled, which is the same treatment a blank cell in the file gets —
        it should read as unfinished rather than pass as complete.
      */}
      {adSets.length > 0 && (
        <div className="shrink-0 border-t border-border-light px-2 py-2">
          <Select
            value=""
            disabled={addRow.isPending}
            onValueChange={(parentId) =>
              addRow.mutate(parentId, {
                onError: () =>
                  notificationService.showError(
                    t('deliveryJobReview.jobPlatformPanel.couldNotAddARow'),
                  ),
              })
            }
          >
            <SelectTrigger
              size="sm"
              className="w-auto border-none text-muted-foreground hover:text-foreground"
              data-testid={`job-add-row-${group.platform}`}
            >
              <PlusIcon className="size-4" aria-hidden />
              {addRow.isPending
                ? t('deliveryJobReview.jobPlatformPanel.adding')
                : t('deliveryJobReview.jobPlatformPanel.addRow')}
            </SelectTrigger>
            <SelectContent>
              {adSets.map((adSet) => (
                <SelectItem key={adSet.id} value={adSet.id}>
                  {nodeName(adSet)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <JobIssuesPanel
        open={isIssuesOpen}
        platform={group.platform}
        issues={issues}
        focusNodeId={issuesNodeId}
        onLinkNode={(nodeId) =>
          linkNode.mutate(nodeId, {
            onSuccess: () =>
              notificationService.showSuccess(t('deliveryJobReview.jobPlatformPanel.linked')),
            onError: () =>
              notificationService.showError(
                t('deliveryJobReview.jobPlatformPanel.couldNotLinkThisRow'),
              ),
          })
        }
        linkingNodeId={linkNode.isPending ? linkNode.variables : null}
        onFocusCell={(entry) => {
          // Only the leaf level has cells in the sheet. A campaign or ad set
          // field lives in the details drawer now, so send those there instead
          // of scrolling to a cell that does not exist.
          if (entry.level !== leafLevelOf(group.platform)) {
            const node = findNode(group, entry.nodeId);
            if (node) {
              setIsIssuesOpen(false);
              setDetails({ nodeId: node.id, focusField: entry.issue.field });
            }
            return;
          }

          // Unfold the path first when grouped: the target row may be inside a
          // collapsed band, and scrolling to an unmounted row does nothing.
          if (grouped) {
            const path = new Set(ancestorIdsOf(group, entry.nodeId));
            setCollapsed((prev) => new Set([...prev].filter((id) => !path.has(id))));
          }
          // eslint-disable-next-line i18next/no-literal-string -- the platform's own field key
          setFocusedCell(`${entry.nodeId}:${entry.issue.field ?? 'name'}`);
        }}
        onClose={() => setIsIssuesOpen(false)}
      />

      <JobNodeDrawer
        node={detailsNode}
        platform={group.platform}
        focusField={details?.focusField ?? null}
        showIssues={showIssues}
        preflightStatus={preflightStatus}
        onEditField={onEditField}
        onClose={() => setDetails(null)}
      />

      <CreativePreviewDialog
        jobId={jobId}
        platform={group.platform}
        node={previewNode}
        onClose={() => setPreviewNodeId(null)}
      />
    </section>
  );
}
